import { useOrderActions } from '@klear/ui/hooks/use-order-actions'
import {
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_VOID,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  ORDER_EVENT_ENTITIES,
  PERMISSION_ENTITY_PAYMENT
} from '@klear/constants'
import * as changeCase from 'change-case'
import type { OrderEventActionFormValues } from '@klear/order-event/event-form'
import {
  ORDER_EVENT_ACTION_BULK_TYPES,
  type OrderEventActionBulkEnum,
  type OrderEventActionEnum,
  type OrderEventType
} from '@klear/order-event/constants'
import { orderEventBulkActionLabel } from '@klear/order-event/presentation'
import type { User } from '@klear/user'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'
import isError from 'lodash/isError'

export interface BulkActionResult {
  success: OrderEventRef[]
  failures: { event: OrderEventRef; error: Error }[]
}

export interface ActionConfig {
  description: string
  action: (events: OrderEventRef[], actionSchema: OrderEventActionFormValues) => Promise<BulkActionResult>
}

export function useEventActionConfig() {
  const {
    deleteEvent,
    publishEvent,
    approveEvent,
    denyEvent,
    acceptEvent,
    rejectEvent,
    fundEvent,
    completeEvent,
    voidEvent
  } = useOrderActions()

  const executeBulkAction = async (
    events: OrderEventRef[],
    actionSchema: OrderEventActionFormValues,
    actionFn: (orderId: number, event: OrderEventRef, actionSchema: OrderEventActionFormValues) => Promise<unknown>
  ): Promise<BulkActionResult> => {
    const success: OrderEventRef[] = []
    const failures: { event: OrderEventRef; error: Error }[] = []

    for (const event of events) {
      try {
        await actionFn(event.orderId, event, actionSchema)
        success.push(event)
      } catch (error) {
        failures.push({
          event,
          error: isError(error) ? error : new Error(`${error}`)
        })
      }
    }

    return { success, failures }
  }

  function getAvailableActionsBulk(user: User, opts?: { exclude?: OrderEventActionBulkEnum[] }) {
    let actions: OrderEventActionBulkEnum[] = ORDER_EVENT_ACTION_BULK_TYPES.filter((action) =>
      ORDER_EVENT_ENTITIES.some((entity) => user.hasPermission(entity, action))
    )
    const { exclude } = opts || {}
    if (exclude) {
      actions = actions.filter((a) => !exclude.includes(a))
    }
    return actions.map((action: OrderEventActionBulkEnum) => ({
      label: orderEventBulkActionLabel(action),
      value: action
    }))
  }

  const getActionConfig = (eventType?: OrderEventType): Partial<Record<OrderEventActionEnum, ActionConfig>> => ({
    [PERMISSION_ACTION_DELETE]: {
      description: `This will permanently delete the ${eventType ? changeCase.noCase(eventType) : 'event'}.`,
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, deleteEvent)
      }
    },
    [PERMISSION_ACTION_PUBLISH]: {
      description:
        eventType === PERMISSION_ENTITY_PAYMENT
          ? 'This will schedule the payment to be sent on the specified date.'
          : eventType === PERMISSION_ENTITY_PURCHASE_ORDER
            ? `This will lock the ${eventType ? changeCase.noCase(eventType) : 'event'} and submit it for approval.`
            : `This will show the ${eventType ? changeCase.noCase(eventType) : 'event'} to the trader.`,
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, publishEvent)
      }
    },
    [PERMISSION_ACTION_APPROVE]: {
      description: 'This confirms that the uploaded documents match the data that was entered.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, approveEvent)
      }
    },
    [PERMISSION_ACTION_DENY]: {
      description: 'This indicates that the uploaded documents do not match the entered data and requires correction.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, denyEvent)
      }
    },
    [PERMISSION_ACTION_ACCEPT]: {
      description:
        eventType === PERMISSION_ENTITY_INVOICE_OFFER
          ? 'This accepts the quote. Klear reviews the purchase order, invoice and proof of acceptance before funding.'
          : 'This accepts the offer.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, acceptEvent)
      }
    },
    [PERMISSION_ACTION_REJECT]: {
      description: 'This rejects the invoice offer.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, rejectEvent)
      }
    },
    [PERMISSION_ACTION_FUND]: {
      description:
        eventType === PERMISSION_ENTITY_INVOICE_OFFER
          ? 'This schedules the advance disbursement payment. The offer will be marked funded once the payment posts.'
          : 'This funds the invoice offer.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, fundEvent)
      }
    },
    [PERMISSION_ACTION_COMPLETE]: {
      description: 'This completes the invoice offer.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, completeEvent)
      }
    },
    [PERMISSION_ACTION_VOID]: {
      description: 'This voids the invoice offer.',
      action: async (events, actionSchema) => {
        return await executeBulkAction(events, actionSchema, voidEvent)
      }
    }
  })

  return { getActionConfig, getAvailableActionsBulk }
}
